import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { isLoggedIn } from "../../auth/authStorage";
import {
  buildExplorerLoginRedirectQuery,
  explorerSignupPath,
  persistExplorerProfileFromSession,
} from "../../explorer/explorerProfileSync";
import {
  clearExplorerPendingOnboarding,
  getExplorerEventId,
  getExplorerFlow,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
  setExplorerPendingOnboarding,
  setExplorerStep2Persisted,
} from "../../explorer/explorerSession";
import Select from "react-select";
import { SORTED_SECTORS } from "../../utils/sectors";
import ExplorerWizardShell from "./ExplorerWizardShell";
import { useTranslation } from "react-i18next";
import {
  EVENT_TERMS_CLOSING,
  EVENT_TERMS_INTRO,
  EVENT_TERMS_SECTIONS,
} from "../auth/eventRegistrationTerms";
import { EXPLORER_SEGMENT_OPTIONS, isAllowedExplorerSegment, normalizeExplorerSegment, stageLabelForExplorerSegment } from "../../explorer/explorerSegments";

/** Stored as Explorer onboarding segment (string). */
const DESCRIBES_YOU_OPTIONS = EXPLORER_SEGMENT_OPTIONS;

const OBJECTIVES_FOR_ATTENDING_KEY = "objectivesForAttending";
const STAGE_OF_BUSINESS_KEY = "stageOfBusiness";
const SECTORS_OF_INTEREST_KEY = "sectorsOfInterest";
const PHOTO_SHARING_CONSENT_KEY = "photoSharingConsent";
const TERMS_ATTACHMENT_CONSENT_KEY = "termsAttachmentConsent";

const OBJECTIVES_FOR_ATTENDING_OPTIONS = [
  { value: "b2b_lead_generation", label: "B2B lead generation" },
  { value: "finding_reliable_b2b_services", label: "Finding reliable B2B services" },
  { value: "getting_mentorship_advice", label: "Getting mentorship/advice" },
  { value: "supporting_muslim_businesses", label: "Supporting Muslim businesses" },
  {
    value: "finding_investment_opportunities",
    label: "Finding investment opportunities",
  },
  { value: "meeting_investors", label: "Meeting investors" },
  {
    value: "meeting_online_connections_offline",
    label: "Meeting online connections offline",
  },
  { value: "learning_more_about_smbf", label: "Learning more about SMBF" },
  { value: "general_networking", label: "General networking" },
  { value: "exploring_partnerships", label: "Exploring partnerships" },
  { value: "other", label: "Other" },
] as const;

const SECTOR_OPTIONS = SORTED_SECTORS.map((s) => ({ value: s, label: s }));

function parseStoredList(value?: string): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function ExplorerSegmentation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pendingInitial = useMemo(() => getExplorerPendingOnboarding(), []);
  const [segment, setSegment] = useState(() =>
    normalizeExplorerSegment(pendingInitial?.segment) || pendingInitial?.segment || "",
  );
  const [objectivesForAttending, setObjectivesForAttending] = useState(
    () => pendingInitial?.discoveryAnswers?.[OBJECTIVES_FOR_ATTENDING_KEY] ?? "",
  );
  const [sectorsOfInterest, setSectorsOfInterest] = useState<string[]>(() =>
    parseStoredList(pendingInitial?.discoveryAnswers?.[SECTORS_OF_INTEREST_KEY]),
  );
  const [photoSharingConsent, setPhotoSharingConsent] = useState(
    () => pendingInitial?.discoveryAnswers?.[PHOTO_SHARING_CONSENT_KEY] === "true",
  );
  const [termsAttachmentConsent, setTermsAttachmentConsent] = useState(
    () => pendingInitial?.discoveryAnswers?.[TERMS_ATTACHMENT_CONSENT_KEY] === "true",
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const fieldInvalid = useMemo(() => {
    if (!showFieldErrors) {
      return {
        segment: false,
        objectives: false,
        photo: false,
        terms: false,
      };
    }
    return {
      segment: !isAllowedExplorerSegment(segment),
      objectives: !objectivesForAttending,
      photo: !photoSharingConsent,
      terms: !termsAttachmentConsent,
    };
  }, [
    showFieldErrors,
    segment,
    objectivesForAttending,
    photoSharingConsent,
    termsAttachmentConsent,
  ]);

  useEffect(() => {
    setError(null);
  }, [
    segment,
    objectivesForAttending,
    photoSharingConsent,
    termsAttachmentConsent,
  ]);

  const authenticated = isLoggedIn();

  useEffect(() => {
    const local = getExplorerLocalProfile();
    if (!local || !local.firstName?.trim() || !local.lastName?.trim()) {
      navigate(explorerSignupPath(), { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const missingFields =
      !isAllowedExplorerSegment(segment) ||
      !objectivesForAttending ||
      !photoSharingConsent ||
      !termsAttachmentConsent;

    if (missingFields) {
      setShowFieldErrors(true);
      setError(t("explorer.segmentation.completeRequired"));
      return;
    }
    setShowFieldErrors(false);
    setError(null);

    const canonicalSegment = normalizeExplorerSegment(segment) || segment;
    const discoveryAnswers = {
      [OBJECTIVES_FOR_ATTENDING_KEY]: objectivesForAttending,
      [STAGE_OF_BUSINESS_KEY]: stageLabelForExplorerSegment(canonicalSegment),
      [SECTORS_OF_INTEREST_KEY]: sectorsOfInterest.join(", "),
      [PHOTO_SHARING_CONSENT_KEY]: String(photoSharingConsent),
      [TERMS_ATTACHMENT_CONSENT_KEY]: String(termsAttachmentConsent),
    };
    const existing = getExplorerPendingOnboarding();
    const mergedPending = {
      ...existing,
      segment: canonicalSegment,
      discoveryAnswers: {
        ...existing?.discoveryAnswers,
        ...discoveryAnswers,
      },
    };
    setExplorerPendingOnboarding(mergedPending);

    const local = getExplorerLocalProfile();
    const flow = getExplorerFlow();
    const eventIdStr = getExplorerEventId();
    const eventIdNum = eventIdStr ? parseInt(eventIdStr, 10) : NaN;
    const safeEventId = Number.isFinite(eventIdNum) ? eventIdNum : undefined;

    if (!local && !isLoggedIn()) {
      setShowFieldErrors(false);
      setError(t("explorer.segmentation.profileMissing"));
      return;
    }

    if (!local && isLoggedIn()) {
      setShowFieldErrors(false);
      setError(t("explorer.segmentation.profileMissing"));
      navigate(explorerSignupPath(), { replace: true });
      return;
    }

    if (!local?.firstName?.trim() || !local?.lastName?.trim()) {
      setShowFieldErrors(false);
      setError(t("explorer.signup.firstLastNameRequired"));
      navigate(explorerSignupPath(), { replace: true });
      return;
    }

    setLoading(true);
    try {
      if (local) {
        try {
          await persistExplorerProfileFromSession({
            segment: canonicalSegment,
            objectives: objectivesForAttending,
            discoveryAnswers: mergedPending.discoveryAnswers,
          });
        } catch (err: unknown) {
          const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
          if (code === "already_registered" && !isLoggedIn()) {
            navigate(`/?${buildExplorerLoginRedirectQuery(local, "/explorer/segmentation", "login")}`, {
              replace: true,
            });
            return;
          }
          throw err;
        }
      }

      if (isLoggedIn()) {
        await saveExplorerOnboarding({
          segment: canonicalSegment,
          flowType: flow,
          discoveryAnswers: mergedPending.discoveryAnswers,
          eventId: safeEventId,
        });
        clearExplorerPendingOnboarding();
      }

      setExplorerStep2Persisted();
      navigate("/explorer/checkout", { replace: true });
    } catch (err: unknown) {
      const apiCode = (err as { response?: { data?: { code?: string; error?: unknown } } })
        ?.response?.data?.code;
      if (apiCode === "profile_required" || apiCode === "profile_incomplete") {
        setError(t("explorer.segmentation.profileMissing"));
        navigate(explorerSignupPath(), { replace: true });
        return;
      }
      if (apiCode === "segmentation_incomplete") {
        setShowFieldErrors(true);
        setError(t("explorer.segmentation.completeRequired"));
        return;
      }
      let message = t("explorer.segmentation.saveFailed");
      if (err && typeof err === "object" && "response" in err) {
        const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
        if (data?.error && typeof data.error === "string") message = data.error;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExplorerWizardShell
      step={2}
      totalSteps={3}
      progressLabel={t("explorer.segmentation.roleQuestion")}
      nextFormId="explorer-segmentation-form"
      nextLabel={t("auth.continue")}
      loading={loading}
      nextDisabled={loading}
      onBack={() =>
        navigate(authenticated ? "/dashboard" : explorerSignupPath(), {
          replace: true,
        })
      }
      error={error}
    >
      <form id="explorer-segmentation-form" onSubmit={onSubmit}>
        <h1 className="fw-bold text-dark mb-2" style={{ fontSize: 22 }}>
          Step 2: {t("explorer.segmentation.quickQuestion")}
        </h1>
        <p className="text-secondary small mb-0">
          {t("explorer.segmentation.subtitle")}
        </p>
        <div className="explorer-wizard-form-stack mt-3 d-flex flex-column gap-3">
          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading small fw-bold text-uppercase text-secondary mb-3">
              {t("explorer.segmentation.aboutYou")}
            </div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label
                  className="form-label small fw-semibold text-secondary mb-2"
                  htmlFor="explorer-describes-you"
                >
                  {t("explorer.segmentation.whichDescribesYou")}
                </label>
                <select
                  id="explorer-describes-you"
                  className={`form-select${fieldInvalid.segment ? " is-invalid" : ""}`}
                  value={normalizeExplorerSegment(segment) || segment}
                  onChange={(e) => setSegment(e.target.value)}
                  aria-label={t("explorer.segmentation.whichDescribesYou")}
                  aria-invalid={fieldInvalid.segment}
                >
                  <option value="">{t("explorer.segmentation.selectOption")}</option>
                  {DESCRIBES_YOU_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label
                  className="form-label small fw-semibold text-secondary mb-2"
                  htmlFor="explorer-objectives-for-attending"
                >
                  {t("explorer.segmentation.objectivesForAttending")}
                </label>
                <select
                  id="explorer-objectives-for-attending"
                  className={`form-select${fieldInvalid.objectives ? " is-invalid" : ""}`}
                  value={objectivesForAttending}
                  onChange={(e) => setObjectivesForAttending(e.target.value)}
                  aria-label={t("explorer.segmentation.objectivesForAttending")}
                  aria-invalid={fieldInvalid.objectives}
                >
                  <option value="">{t("explorer.segmentation.selectObjective")}</option>
                  {OBJECTIVES_FOR_ATTENDING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading small fw-bold text-uppercase text-secondary mb-3">
              {t("explorer.segmentation.preferences")}
            </div>
            <label
              className="form-label small fw-semibold text-secondary mb-2"
              htmlFor="explorer-sectors-of-interest"
            >
              {t("events.sectorsOfInterest")}{" "}
              <span className="fw-normal text-muted">({t("explorer.segmentation.optional")})</span>
            </label>
            <div className="explorer-wizard-react-select">
              <Select
                inputId="explorer-sectors-of-interest"
                options={SECTOR_OPTIONS}
                isMulti
                isClearable
                placeholder={t("explorer.segmentation.selectSectors")}
                value={sectorsOfInterest.map((val) => ({ value: val, label: val }))}
                onChange={(selected) => {
                  const values = (selected as { value: string; label: string }[] | null)?.map(
                    (opt) => opt.value,
                  );
                  setSectorsOfInterest(values ?? []);
                }}
                classNamePrefix="react-select"
                aria-label={t("explorer.segmentation.sectorsOptionalAria")}
              />
            </div>
          </div>

          <div className="explorer-wizard-form-panel mb-0">
            <div className="explorer-wizard-section-heading small fw-bold text-uppercase text-secondary mb-3">
              {t("explorer.segmentation.termsConsent")}
            </div>
            <p className="small text-secondary mb-3 mb-md-2">
              {t("explorer.segmentation.reviewTerms")}
            </p>
            <div
              className="explorer-wizard-terms-preview"
              role="region"
              aria-label={t("explorer.segmentation.termsAndConditions")}
              tabIndex={0}
            >
              <div className="explorer-wizard-terms-content">
                <p className="fw-bold mb-1 text-body">{t("footer.eventPolicy")}</p>
                <p className="fw-bold mb-3 text-body">{t("explorer.segmentation.effectiveDate")}</p>
                <p className="mb-3 mb-0">{EVENT_TERMS_INTRO}</p>
                {EVENT_TERMS_SECTIONS.map((section) => (
                  <div key={section.title} className="mb-3">
                    <p className="fw-bold mb-1 mb-0 text-body">{section.title}</p>
                    <p className="mb-0">{section.body}</p>
                  </div>
                ))}
                <p className="mb-3 mb-0">{EVENT_TERMS_CLOSING}</p>
                <p className="fw-bold mb-1 mb-0 text-body">{t("explorer.segmentation.smbfLtd")}</p>
                <p className="fw-bold mb-0 text-body">{t("explorer.segmentation.effectiveDate")}</p>
              </div>
            </div>

            <hr className="explorer-wizard-consent-divider border-secondary-subtle my-4 opacity-100" />

            <div
              className={`explorer-wizard-consent-row form-check mb-3${
                fieldInvalid.photo ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                id="explorer-photo-consent"
                className={`form-check-input mt-1${fieldInvalid.photo ? " is-invalid" : ""}`}
                type="checkbox"
                checked={photoSharingConsent}
                onChange={(e) => setPhotoSharingConsent(e.target.checked)}
                aria-invalid={fieldInvalid.photo}
              />
              <label className="form-check-label small text-body-secondary" htmlFor="explorer-photo-consent">
                {t("explorer.segmentation.photoConsentText")}
              </label>
            </div>
            <div
              className={`explorer-wizard-consent-row form-check mb-0${
                fieldInvalid.terms ? " explorer-wizard-consent-row--invalid" : ""
              }`}
            >
              <input
                id="explorer-terms-attachment-consent"
                className={`form-check-input mt-1${fieldInvalid.terms ? " is-invalid" : ""}`}
                type="checkbox"
                checked={termsAttachmentConsent}
                onChange={(e) => setTermsAttachmentConsent(e.target.checked)}
                aria-invalid={fieldInvalid.terms}
              />
              <label
                className="form-check-label small text-body-secondary"
                htmlFor="explorer-terms-attachment-consent"
              >
                {t("explorer.segmentation.agreeTerms")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </ExplorerWizardShell>
  );
}

export default ExplorerSegmentation;
